/**
 * yokumoku.max-cart-pcs (カート投入個数上限) 共通バリデーションヘルパー
 *
 * 商品メタフィールド `yokumoku.max-cart-pcs` (number_integer, min:1) に基づき
 * カート内の同一商品(product_id)合計数量が上限を超えないようチェックする。
 *
 * データソース:
 *   - DOM上の data-product-id + data-max-cart-pcs 属性（各サーフェスのLiquidで埋め込み）
 *   - window.__yokuProductLimits = { [productId]: maxCartPcs } （明示的に注入する場合）
 *
 * 使い方:
 *   await window.MaxCartPcs.validateAdd({ productId, addQty, productTitle })
 *     → 上限超過時は alert を出し false を返す
 *
 *   await window.MaxCartPcs.validateCart()
 *     → カート内の全商品が上限内か検証 (W-check用)
 */
(function () {
  if (window.MaxCartPcs) return;

  const ALERT_MSG = 'この商品はこれ以上カートに入れられません。';

  function buildLimitMsg(limit) {
    return 'この商品は' + limit + '個までご購入いただけます。数量を' + limit + '個以下に変更してください。';
  }
  const limitMap = new Map(); // productId(string) → max(int)

  function setLimit(productId, max) {
    if (productId == null) return;
    const n = parseInt(max, 10);
    if (Number.isFinite(n) && n >= 1) {
      limitMap.set(String(productId), n);
    }
  }

  function getLimit(productId) {
    if (productId == null) return null;
    const v = limitMap.get(String(productId));
    return Number.isFinite(v) ? v : null;
  }

  function collectFromDom(root) {
    const scope = root || document;
    // data-base-product-id を優先（CollectionボタンはdataProductId=variantIdのため）
    scope.querySelectorAll('[data-max-cart-pcs]').forEach((el) => {
      const pid = el.getAttribute('data-base-product-id') || el.getAttribute('data-product-id');
      const max = el.getAttribute('data-max-cart-pcs');
      if (pid && max) setLimit(pid, max);
    });
    if (window.__yokuProductLimits && typeof window.__yokuProductLimits === 'object') {
      Object.keys(window.__yokuProductLimits).forEach((k) => {
        setLimit(k, window.__yokuProductLimits[k]);
      });
    }
  }

  let _cartCache = null;
  let _cartCacheTs = 0;
  const CART_CACHE_MS = 1500;

  async function fetchCart(force) {
    const now = Date.now();
    if (!force && _cartCache && now - _cartCacheTs < CART_CACHE_MS) return _cartCache;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(root + 'cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart fetch failed: ' + res.status);
    _cartCache = await res.json();
    _cartCacheTs = now;
    return _cartCache;
  }

  function invalidateCartCache() {
    _cartCache = null;
    _cartCacheTs = 0;
  }

  function sumQtyForProduct(cart, productId) {
    if (!cart || !cart.items || productId == null) return 0;
    const target = String(productId);
    let total = 0;
    for (const item of cart.items) {
      if (String(item.product_id) === target) total += item.quantity;
    }
    return total;
  }

  function showAlert(msg) {
    try {
      window.alert(msg || ALERT_MSG);
    } catch (e) {
      console.warn('[MaxCartPcs] alert blocked:', e);
    }
  }

  /**
   * 単品追加前のチェック
   * @param {Object} opts
   * @param {string|number} opts.productId - 商品ID (必須)
   * @param {number} opts.addQty - 追加数量
   * @param {string} [opts.productTitle] - エラー表示用 (現状未使用)
   * @param {Object} [opts.cart] - 既知のcart状態 (未指定なら /cart.js を取得)
   * @returns {Promise<boolean>} 上限内なら true、超過時は alert を出して false
   */
  async function validateAdd(opts) {
    opts = opts || {};
    const productId = opts.productId;
    const addQty = parseInt(opts.addQty, 10) || 1;
    if (productId == null) return true;
    const limit = getLimit(productId);
    if (limit == null) return true;
    let cart = opts.cart;
    try {
      if (!cart) cart = await fetchCart();
    } catch (e) {
      console.warn('[MaxCartPcs] cart fetch failed, skip check', e);
      return true;
    }
    const current = sumQtyForProduct(cart, productId);
    if (current + addQty > limit) {
      showAlert(buildLimitMsg(limit));
      return false;
    }
    return true;
  }

  /**
   * 複数商品の同時追加（セット商品など）
   * @param {Array<{productId, quantity}>} items
   * @returns {Promise<boolean>}
   */
  async function validateAddMultiple(items) {
    if (!Array.isArray(items) || items.length === 0) return true;
    let cart;
    try { cart = await fetchCart(); } catch (e) { return true; }
    // 同一productIdへの追加分を集計
    const addByProduct = {};
    for (const it of items) {
      if (!it || it.productId == null) continue;
      const k = String(it.productId);
      addByProduct[k] = (addByProduct[k] || 0) + (parseInt(it.quantity, 10) || 1);
    }
    for (const pid of Object.keys(addByProduct)) {
      const limit = getLimit(pid);
      if (limit == null) continue;
      const current = sumQtyForProduct(cart, pid);
      if (current + addByProduct[pid] > limit) {
        showAlert(buildLimitMsg(limit));
        return false;
      }
    }
    return true;
  }

  /**
   * 数量変更時のチェック (cart drawer/page +/-、shipping +/-)
   * @param {Object} opts
   * @param {string|number} opts.productId
   * @param {string|number} [opts.variantId] - 変更対象variant
   * @param {string|number} [opts.itemKey] - cart line key
   * @param {number} opts.newQty - 新しい数量
   * @param {Object} [opts.cart]
   * @returns {Promise<boolean>}
   */
  async function validateChange(opts) {
    opts = opts || {};
    const productId = opts.productId;
    const newQty = parseInt(opts.newQty, 10);
    if (productId == null || !Number.isFinite(newQty)) return true;
    const limit = getLimit(productId);
    if (limit == null) return true;
    let cart = opts.cart;
    try {
      if (!cart) cart = await fetchCart();
    } catch (e) { return true; }
    // 対象ライン以外の同一productIdの合計
    let other = 0;
    if (cart && cart.items) {
      for (const item of cart.items) {
        if (String(item.product_id) !== String(productId)) continue;
        if (opts.itemKey && item.key === opts.itemKey) continue;
        if (!opts.itemKey && opts.variantId && String(item.variant_id) === String(opts.variantId)) continue;
        other += item.quantity;
      }
    }
    if (other + newQty > limit) {
      showAlert(buildLimitMsg(limit));
      return false;
    }
    return true;
  }

  /**
   * カート全体のWチェック (お手続きに進む直前)
   * @param {Object} [cart] 既知のcart状態
   * @returns {Promise<{ok: boolean, violations: Array}>}
   */
  async function validateCart(cart) {
    if (!cart) {
      try { cart = await fetchCart(true); } catch (e) { return { ok: true, violations: [] }; }
    }
    const violations = [];
    if (!cart || !cart.items) return { ok: true, violations };
    const totals = new Map();
    for (const item of cart.items) {
      const k = String(item.product_id);
      totals.set(k, (totals.get(k) || 0) + item.quantity);
    }
    for (const [pid, qty] of totals) {
      const limit = getLimit(pid);
      if (limit != null && qty > limit) {
        const sample = cart.items.find((i) => String(i.product_id) === pid);
        violations.push({ productId: pid, quantity: qty, limit, productTitle: sample ? sample.product_title : '' });
      }
    }
    if (violations.length) {
      // 上限超過商品ごとに詳細メッセージを付加
      let msg = '購入上限数を超えている商品があります。数量をご確認のうえ、再度お手続きください。';
      violations.forEach(function (v) {
        msg += '\n' + buildLimitMsg(v.limit);
      });
      showAlert(msg);
    }
    return { ok: violations.length === 0, violations };
  }

  function init() {
    collectFromDom();
    // cart更新イベントでキャッシュ無効化
    document.addEventListener('cart:updated', invalidateCartCache);
    document.addEventListener('cart:refresh', invalidateCartCache);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.MaxCartPcs = {
    setLimit,
    getLimit,
    collectFromDom,
    invalidateCartCache,
    validateAdd,
    validateAddMultiple,
    validateChange,
    validateCart,
    ALERT_MSG,
  };
})();
